import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle, ArrowRight, Home } from "lucide-react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const navigate = useNavigate();

  useEffect(() => {
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("failed"); return; }
      } catch { /* keep trying */ }
      tries += 1;
      if (tries > 12) { setStatus("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    if (sid) poll(); else setStatus("timeout");
    return () => clearTimeout(timer);
  }, [sid]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center" data-testid="payment-success">
        {status === "checking" && (
          <>
            <Loader2 className="h-10 w-10 animate-spin text-brand mx-auto" />
            <h1 className="font-head text-xl font-semibold text-ink mt-4">Confirming your payment…</h1>
            <p className="text-ink-soft mt-1">This only takes a moment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="h-14 w-14 rounded-full bg-green-50 flex items-center justify-center mx-auto">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="font-head text-2xl font-bold text-ink mt-4">Order confirmed</h1>
            <p className="text-ink-soft mt-2">Your genetic test is ordered for home sample collection. Status is now <span className="font-semibold text-[#5B21B6]">In Lab</span> until your report is uploaded.</p>
            <button onClick={() => navigate("/patient")} data-testid="success-continue"
              className="mt-6 inline-flex items-center gap-2 rounded-lg bg-brand text-white px-6 py-2.5 font-semibold hover:bg-brand-hover transition-colors">
              Back to dashboard <ArrowRight className="h-4 w-4" />
            </button>
          </>
        )}
        {(status === "timeout" || status === "failed") && (
          <>
            <div className="h-14 w-14 rounded-full bg-amber-50 flex items-center justify-center mx-auto">
              <XCircle className="h-8 w-8 text-amber-600" />
            </div>
            <h1 className="font-head text-xl font-semibold text-ink mt-4">
              {status === "failed" ? "Payment not completed" : "Still processing"}
            </h1>
            <p className="text-ink-soft mt-2">
              {status === "failed"
                ? "Your payment didn't go through. You can try ordering again from your dashboard."
                : "We couldn't confirm the payment yet. If you completed it, your dashboard will update shortly."}
            </p>
            <button onClick={() => navigate("/patient")}
              className="mt-6 inline-flex items-center gap-2 rounded-lg bg-brand text-white px-6 py-2.5 font-semibold hover:bg-brand-hover transition-colors">
              <Home className="h-4 w-4" /> Back to dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center" data-testid="payment-cancel">
        <div className="h-14 w-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto">
          <XCircle className="h-8 w-8 text-slate-500" />
        </div>
        <h1 className="font-head text-xl font-semibold text-ink mt-4">Payment cancelled</h1>
        <p className="text-ink-soft mt-2">No charge was made. You can order the genetic test any time from your dashboard.</p>
        <Link to="/patient" className="mt-6 inline-flex items-center gap-2 rounded-lg bg-brand text-white px-6 py-2.5 font-semibold hover:bg-brand-hover transition-colors">
          Back to dashboard <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
